/**
 * A deliberately small Markdown renderer for the user guide.
 *
 * The guide is authored once, as public/guide.md, so that AI agents can fetch
 * it as plain text and people can read it rendered in the app. A Markdown
 * library would be the larger dependency for one trusted, in-repo file, so
 * this handles exactly the subset the guide uses: headings, rules,
 * paragraphs, lists, tables, and inline bold / italic / code / links.
 *
 * Everything becomes React elements — no innerHTML — so nothing in the file
 * can inject markup.
 */
import type { ReactNode } from 'react';

export interface Heading {
  id: string;
  text: string;
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/** The `##` headings, for a table of contents. */
export function headings(source: string): Heading[] {
  return source
    .split('\n')
    .filter((l) => l.startsWith('## '))
    .map((l) => {
      const text = l.slice(3).trim();
      return { id: slugify(text), text };
    });
}

export function Markdown({ source }: { source: string }) {
  return <>{blocks(source.replace(/\r\n/g, '\n').split('\n'))}</>;
}

function blocks(lines: string[]): ReactNode[] {
  const out: ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i]!;
    const key = out.length;

    if (line.trim() === '') {
      i++;
    } else if (line.trim() === '---') {
      out.push(<hr key={key} />);
      i++;
    } else if (/^#{1,3} /.test(line)) {
      const level = line.indexOf(' ');
      const text = line.slice(level + 1).trim();
      const Tag = (['h1', 'h2', 'h3'] as const)[level - 1]!;
      out.push(
        <Tag key={key} id={level === 2 ? slugify(text) : undefined}>
          {inline(text)}
        </Tag>,
      );
      i++;
    } else if (line.startsWith('|')) {
      const rows: string[] = [];
      while (i < lines.length && lines[i]!.startsWith('|')) rows.push(lines[i++]!);
      out.push(<Table key={key} rows={rows} />);
    } else if (/^(- |\d+\. )/.test(line)) {
      const ordered = /^\d+\. /.test(line);
      const items: string[] = [];
      while (i < lines.length && /^(- |\d+\. )/.test(lines[i]!)) {
        items.push(lines[i++]!.replace(/^(- |\d+\. )/, ''));
      }
      const List = ordered ? 'ol' : 'ul';
      out.push(
        <List key={key}>
          {items.map((t, j) => (
            <li key={j}>{inline(t)}</li>
          ))}
        </List>,
      );
    } else {
      const para: string[] = [];
      while (i < lines.length && lines[i]!.trim() !== '' && !/^(#|\||- |\d+\. |---)/.test(lines[i]!)) {
        para.push(lines[i++]!);
      }
      out.push(<p key={key}>{inline(para.join(' '))}</p>);
    }
  }
  return out;
}

function Table({ rows }: { rows: string[] }) {
  const cells = (row: string) =>
    row
      .trim()
      .replace(/^\||\|$/g, '')
      .split('|')
      .map((c) => c.trim());
  const [head, , ...body] = rows; // the second row is the |---| separator
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            {cells(head ?? '').map((c, j) => (
              <th key={j}>{inline(c)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((r, i) => (
            <tr key={i}>
              {cells(r).map((c, j) => (
                <td key={j}>{inline(c)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** `code`, **bold**, *italic*, [text](href) — in that precedence. */
function inline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const pattern = /`([^`]+)`|\*\*(.+?)\*\*|\*(.+?)\*|\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0;
  for (const m of text.matchAll(pattern)) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const key = out.length;
    if (m[1] !== undefined) out.push(<code key={key}>{m[1]}</code>);
    else if (m[2] !== undefined) out.push(<strong key={key}>{inline(m[2])}</strong>);
    else if (m[3] !== undefined) out.push(<em key={key}>{inline(m[3])}</em>);
    else out.push(<a key={key} href={m[5]}>{m[4]}</a>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}
